import { z } from "zod";

/**
 * Staff password reset (Leon, 2026-10-17; docs/specs/staff-password-reset.md).
 *
 * Main path: a 6-digit code by SMS to the phone verified on the staff record.
 * Fallback: an Admin or Superadmin sets a temporary password that must be
 * changed at the next sign-in.
 */

// Engineering floor, not a business rule: Supabase's own minimum is 6. Change
// it here only — the partner screens and the server both read this.
export const STAFF_PASSWORD_MIN_LENGTH = 8;

export const staffPasswordSchema = z
  .string()
  .min(STAFF_PASSWORD_MIN_LENGTH, `Use at least ${STAFF_PASSWORD_MIN_LENGTH} characters.`)
  .max(72, "Use at most 72 characters.");

const signInEmail = z.string().trim().email("Enter the email you sign in with.").max(254);

export const startStaffPasswordResetSchema = z.object({ email: signInEmail });
export type StartStaffPasswordResetInput = z.infer<typeof startStaffPasswordResetSchema>;

export const confirmStaffPasswordResetSchema = z.object({
  email: signInEmail,
  code: z.string().regex(/^\d{6}$/, "Enter the 6-digit code."),
  password: staffPasswordSchema,
});
export type ConfirmStaffPasswordResetInput = z.infer<typeof confirmStaffPasswordResetSchema>;

export const changeStaffPasswordSchema = z.object({ password: staffPasswordSchema });
export type ChangeStaffPasswordInput = z.infer<typeof changeStaffPasswordSchema>;

/** The admin fallback. How identity was checked goes into the audit row. */
export const adminResetStaffPasswordSchema = z.object({
  identityCheck: z
    .string()
    .trim()
    .min(10, "Say how you checked who this is (at least 10 characters).")
    .max(500),
});
export type AdminResetStaffPasswordInput = z.infer<typeof adminResetStaffPasswordSchema>;
